import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import type { VesselPlan } from './churchModel';
import { vesselButtressRings, vesselOutline } from './churchVessel';
import { conventEnclosureRings } from './conventEnclosure';
import { conventRangeRect } from './conventRange';
import { corners } from './genericBlock';
import reading from './oudeNonnenPlan.json';
import { toLayout, type PlanFrame } from './planFrame';

export function oudeNonnenFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error('Oude Nonnen lacks its component anchor');
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export const oudeNonnenChapel = (): VesselPlan => ({ ...reading.chapel, apse: 'polygonal' });

export function oudeNonnenRangeFrame(
  plan: PlanFrame,
  part: (typeof reading.ranges)[number]
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

export function oudeNonnenFoundations(landmark: Landmark): Ring[] {
  const plan = oudeNonnenFrame(landmark),
    chapel = oudeNonnenChapel();
  return [vesselOutline(chapel), ...vesselButtressRings(chapel)]
    .map((ring) => ring.map((p) => toLayout(plan, p)))
    .concat(
      reading.ranges.flatMap((part) => {
        const rect = conventRangeRect(part),
          frame = oudeNonnenRangeFrame(plan, part),
          rooms = part.passage
            ? [
                { ...rect, u1: -part.passage / 2 },
                { ...rect, u0: part.passage / 2 },
              ]
            : [rect];
        return rooms.map((r) => corners(r).map((p) => toLayout(frame, p)));
      })
    );
}

export function oudeNonnenObstacles(landmark: Landmark): Ring[] {
  const plan = oudeNonnenFrame(landmark);
  return [
    ...oudeNonnenFoundations(landmark),
    ...conventEnclosureRings(reading.walls).map((ring) => ring.map((p) => toLayout(plan, p))),
  ];
}

/** Two source-read cultivated courts, with diagonal paths rather than a repeated square garth. */
export function oudeNonnenFloors(landmark: Landmark) {
  const plan = oudeNonnenFrame(landmark),
    beds: Ring[] = [];
  for (const r of reading.gardens) {
    const u = (r.u0 + r.u1) / 2,
      v = (r.v0 + r.v1) / 2,
      path = 0.65;
    beds.push(
      [
        [r.u0, r.v0 + path],
        [u - path, v],
        [r.u0, r.v1 - path],
      ],
      [
        [r.u0 + path, r.v0],
        [r.u1 - path, r.v0],
        [u, v - path],
      ],
      [
        [r.u1, r.v0 + path],
        [r.u1, r.v1 - path],
        [u + path, v],
      ],
      [
        [r.u0 + path, r.v1],
        [u, v + path],
        [r.u1 - path, r.v1],
      ]
    );
  }
  return [
    ...reading.courts.map((r) => ({ ring: corners(r), height: 0.025, garden: false })),
    ...beds.map((ring) => ({ ring, height: 0.05, garden: true })),
  ].map((f) => ({ ...f, ring: f.ring.map((p) => toLayout(plan, p)) }));
}
